/**
 * Instruction fragments shared across the QuickScore route-specific vision-
 * extraction prompts (bubble/handwritten, single and batch alike). Composed
 * by `${…}` interpolation into each prompt's template literal rather than
 * copy-pasted per prompt, so the wording can only ever drift in one place.
 *
 * Not part of the package barrel — consumed only by sibling files in this `prompts/` directory.
 */

/**
 * The `{ data, confidence }` envelope's per-answer output-format rule. Byte-
 * identical whether the prompt is single-image (`data.answers`) or batch
 * (`data[i].answers`) — the array-of-arrays shape doesn't change with arity.
 */
export const EXTRACTION_ANSWER_FORMAT_INSTRUCTIONS =
  'Each answer is a 3-element array: [questionNumber, answer, confidence]. Do not use objects or field names for answers - arrays only.'

/**
 * The student-name field is handwritten regardless of assessment type
 * (Bubble or Handwritten) or arity (single or batch), so every route prompt
 * carries this identical guidance.
 */
export const EXTRACTION_STUDENT_NAME_INSTRUCTIONS =
  'NAME: the student name is handwritten near the top of the sheet. It may be in "First Last", "Last, First", or a single "Name:" line layout. If the sheet has no labeled name field, treat the first handwritten text near the top as the name. Always return the name in "First Last" order, converting a "Last, First" layout accordingly. If the name region is blank, illegible, or you are not reasonably confident, return null - never guess a name.'

/** Closing best-effort guidance shared by every route prompt. */
export const EXTRACTION_BEST_GUESS_INSTRUCTION =
  'If unsure about any answer, provide your best guess with a lower confidence rather than omitting it.'

/**
 * Per-sheet confidence instruction for the two BATCH prompts' `data[i].confidence` field —
 * distinct from the envelope's own top-level `confidence`, which the model reports once per AI
 * call and previously got copied onto every sheet in that call/chunk regardless of how legible
 * each one actually was. Single-sheet prompts don't need this: their one sheet IS the call, so
 * the envelope confidence already reflects that sheet alone.
 */
export const EXTRACTION_PER_SHEET_CONFIDENCE_INSTRUCTIONS =
  'The top-level "confidence" is your confidence across the WHOLE batch. Each sheet\'s own "confidence" field must reflect only how confidently you read THAT sheet - do not copy the batch-level figure into every sheet; vary each sheet\'s confidence with its own legibility.'

/**
 * Per-sheet confidence instruction for the two SINGLE-sheet prompts' `data.confidence` field.
 * Unlike the batch prompts' `EXTRACTION_PER_SHEET_CONFIDENCE_INSTRUCTIONS`, there is no separate
 * batch-level figure a single-sheet call could wrongly copy — its one sheet IS the whole call, so
 * the envelope's top-level "confidence" and this sheet's own "confidence" describe the same read
 * and are expected to match.
 */
export const EXTRACTION_SINGLE_SHEET_CONFIDENCE_INSTRUCTIONS =
  'The "confidence" field inside "data" is your confidence in reading THIS sheet. Since this call covers only one sheet, it should equal the top-level "confidence" value above.'

/**
 * Guards the row-shift defect: a blank/skipped item that produces no row silently renumbers
 * every answer after it (a student's real answer to question 30 gets recorded as question
 * 29's), and the sheet still comes back with the right total row count, so nothing downstream
 * can tell. Distinct from `EXTRACTION_BEST_GUESS_INSTRUCTION`, which covers an ILLEGIBLE answer
 * only — a blank item was never attempted at all, and still needs its own row.
 *
 * Deliberately does NOT sweep "1 to {{maxQuestion}}" — an Open range folds its whole span into
 * one row at the range's first item number (`buildOpenRangeInstruction`), so `maxQuestion` (the
 * key's highest `endQuestion`/`questionNumber`) can exceed the row count this instruction should
 * actually require whenever a range exists. Coverage is anchored to what's printed on the sheet
 * instead, with an explicit carve-out for range numbers so this instruction and the
 * OPEN-RESPONSE RANGES note (`{{openRangeInstruction}}`, appended later in every prompt that
 * carries one) never contradict each other — keep both in sync if either changes.
 */
export const EXTRACTION_QUESTION_COVERAGE_INSTRUCTIONS =
  "Every question number is PRINTED on the sheet next to its answer - read that printed number for each row; never infer a question's number from its position in your list, especially right after a blank or skipped item. Emit exactly one row for every question number printed on the sheet, in order, with none skipped or duplicated - EXCEPT a number that falls inside an OPEN-RESPONSE RANGE, if the answer key defines one (see the OPEN-RESPONSE RANGES note later in this prompt): a range contributes exactly ONE row, at its first item number only, never one row per number it covers. If a non-range item was left BLANK, still emit that question's row with an empty string \"\" as the answer - never leave a question's row out."
